import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, GHS, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    full_name: user?.name || "", phone: "", region: "", city: "", address: "", notes: "",
  });
  const [subEmail, setSubEmail] = useState(false);
  const [subWhatsapp, setSubWhatsapp] = useState(false);

  useEffect(() => { api.get("/regions").then((r) => setRegions(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.region) { setError("Please select your region"); return; }
    setError(""); setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        ...form,
        subscribe_email: subEmail,
        subscribe_whatsapp: subWhatsapp,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success("Order placed! Pay cash on delivery.");
      navigate(`/orders/${data.order_id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not place order");
    } finally { setLoading(false); }
  };

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-display text-3xl text-brand">Your cart is empty</h1>
        <Button data-testid="checkout-empty-shop" onClick={() => navigate("/shop")} className="mt-6 bg-brand hover:bg-brand-hover text-white rounded-full">Browse Products</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-12">
      <h1 className="font-display text-4xl text-brand">Checkout</h1>
      <p className="text-brand-muted mt-1">Cash on delivery — pay when your order arrives.</p>

      <div className="mt-10 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
        <form onSubmit={submit} className="space-y-5" data-testid="checkout-form">
          <h2 className="font-display text-2xl text-brand">Delivery details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-brand">Full name</Label>
              <Input data-testid="checkout-name" required value={form.full_name} onChange={set("full_name")} className="mt-1.5 h-12 rounded-sm" />
            </div>
            <div>
              <Label className="text-brand">Phone number</Label>
              <Input data-testid="checkout-phone" required value={form.phone} onChange={set("phone")} placeholder="024 000 0000" className="mt-1.5 h-12 rounded-sm" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-brand">Region</Label>
              <Select value={form.region} onValueChange={(v) => setForm((f) => ({ ...f, region: v }))}>
                <SelectTrigger data-testid="checkout-region" className="mt-1.5 h-12 rounded-sm"><SelectValue placeholder="Select region" /></SelectTrigger>
                <SelectContent>
                  {regions.map((r) => <SelectItem key={r} value={r} data-testid={`region-${r}`}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-brand">City / Town</Label>
              <Input data-testid="checkout-city" required value={form.city} onChange={set("city")} className="mt-1.5 h-12 rounded-sm" />
            </div>
          </div>
          <div>
            <Label className="text-brand">Delivery address</Label>
            <Textarea data-testid="checkout-address" required value={form.address} onChange={set("address")} placeholder="House number, street, landmark…" className="mt-1.5 rounded-sm min-h-[90px]" />
          </div>
          <div>
            <Label className="text-brand">Order notes (optional)</Label>
            <Textarea data-testid="checkout-notes" value={form.notes} onChange={set("notes")} className="mt-1.5 rounded-sm" />
          </div>

          <div className="bg-brand-surface rounded-sm p-4" data-testid="checkout-optin">
            <p className="text-sm text-brand font-medium">📩 Keep me updated with tips, offers and news from Yayra Glover Organic Inputs</p>
            <div className="mt-3 space-y-2.5">
              <label className="flex items-center gap-2.5 text-sm text-brand cursor-pointer">
                <input type="checkbox" data-testid="optin-email" checked={subEmail} onChange={(e) => setSubEmail(e.target.checked)} className="w-4 h-4 accent-[#1A3B2A]" />
                via Email
              </label>
              <label className="flex items-center gap-2.5 text-sm text-brand cursor-pointer">
                <input type="checkbox" data-testid="optin-whatsapp" checked={subWhatsapp} onChange={(e) => setSubWhatsapp(e.target.checked)} className="w-4 h-4 accent-[#1A3B2A]" />
                via WhatsApp
              </label>
            </div>
          </div>

          {error && <p className="text-sm text-brand-terracotta" data-testid="checkout-error">{error}</p>}
          <Button type="submit" data-testid="place-order-btn" disabled={loading} className="w-full h-13 bg-brand hover:bg-brand-hover text-white rounded-full text-base">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Place Order · ${GHS(total)}`}
          </Button>
        </form>

        <div className="lg:sticky lg:top-24 h-fit bg-white border border-brand/10 rounded-sm p-6">
          <h2 className="font-display text-2xl text-brand">Order summary</h2>
          <div className="mt-5 space-y-4" data-testid="summary-items">
            {items.map((i) => (
              <div key={i.product_id} className="flex gap-3">
                <img src={i.image} alt={i.name} className="w-14 h-14 object-cover rounded-sm bg-brand-surface" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-brand font-medium leading-tight">{i.name}</p>
                  <p className="text-xs text-brand-muted">{i.quantity} × {GHS(i.price)}</p>
                </div>
                <p className="text-sm font-semibold text-brand">{GHS(i.price * i.quantity)}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-brand/10 mt-5 pt-4 flex justify-between font-semibold text-brand">
            <span>Total</span><span data-testid="summary-total">{GHS(total)}</span>
          </div>
          <div className="mt-5 flex items-start gap-2 text-xs text-brand-muted bg-brand-surface rounded-sm p-3">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" /> Cash on delivery. No payment required now.
          </div>
        </div>
      </div>
    </div>
  );
}
